<template>
  <BaseCard
    title="CIM Analytics"
    class="h-full"
  >
    <template #actions>
      <Dropdown
        v-model="selectedOption"
        :options="options"
        option-label="label"
        option-value="value"
        :disabled="isLoading"
        @change="handleOptionChange"
      />
    </template>
    <template #content>
      <div class="flex flex-col gap-4 min-h-fit">
        <div class="px-4 flex flex-col gap-10">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <!-- Card 1 -->
            <div v-if="isLoading">
              <Skeleton
                height="110px"
                width="100%"
              />
            </div>
            <div
              v-else
              class="bg-gradient-to-br from-primary-600 to-primary-400 p-6 rounded-md"
            >
              <div class="text-3xl font-bold text-white">
                {{ currentPeriod }}
              </div>
              <p class="text-sm text-white mt-1">
                Total CIM Views in Last {{ matchOption }} Days
              </p>
            </div>
            <div v-if="isLoading">
              <Skeleton
                height="110px"
                width="100%"
              />
            </div>
            <div
              v-else
              class="bg-gradient-to-br from-primary-600 to-primary-400 p-6 rounded-md"
            >
              <div class="flex items-baseline gap-2">
                <div class="text-3xl font-bold text-white">
                  {{ percentageChange }}%
                </div>
                <div class="text-sm text-white opacity-80">
                  ({{ previousPeriod }} views in the previous period)
                </div>
              </div>
              <p class="text-sm text-white mt-1">
                {{
                  percentageChange === 0
                    ? "No change in"
                    : percentageChange > 0
                      ? "Increase in"
                      : "Decrease in"
                }} CIM views compared to previous period
              </p>
            </div>
          </div>

          <div v-if="isLoading">
            <Skeleton
              height="400px"
              width="100%"
            />
          </div>

          <!-- Chart Section -->
          <div
            v-else
            class="flex flex-col gap-4 w-full border p-4 rounded-lg"
          >
            <span class="text-lg lg:text-2xl font-bold text-gray-700">
              Most Viewed CIMs - Last {{ matchOption }} Days
            </span>
            <Bar
              id="financialAnalysis"
              class=" w-full min-h-[500px] max-h-[500px]"
              :options="chartOptions"
              :data="chartData"
            />
          </div>

          <!-- Table Section -->
          <div class="flex flex-col gap-4 w-full border p-4 rounded-lg">
            <span class="text-lg lg:text-2xl font-bold text-gray-700">
              Top 5 Viewed CIMs - Last {{ matchOption }} Days
            </span>
            <Skeleton
              v-if="isLoading"
              height="400px"
              width="100%"
            />
            <DataTable
              v-else
              :value="businessesData"
              class="p-datatable-striped"
            >
              <Column header="Business Name">
                <template #body="slotProps">
                  <a
                    href="#"
                    @click.prevent="openBusiness(slotProps.data.businessId)"
                  >
                    {{ slotProps.data.businessName }}
                  </a>
                </template>
              </Column>
              <Column
                field="count"
                header="Views Count"
              />

              <Column
                header=""
                class="text-center"
                style="min-width: 100px; max-width: 100px"
              >
                <template #body="slotProps">
                  <Button
                    label="View Analytics"
                    icon="pi pi-external-link"
                    @click="openBusiness(slotProps?.data?.businessId)"
                  />
                </template>
              </Column>
            </DataTable>
          </div>
        </div>
      </div>
    </template>
  </BaseCard>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { Bar } from 'vue-chartjs'
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

interface BusinessAnalytics {
  businessId: string
  businessName: string
  count: number
}

interface AnalyticsData {
  top20ForCurrentPeriod: BusinessAnalytics[]
  top20ForPreviousPeriod: BusinessAnalytics[]
  totalCountForCurrentPeriod: number
  totalCountForPreviousPeriod: number
}

const isLoading = ref(true)
const analyticsData = ref<AnalyticsData | null>(null)

const currentPeriod = computed(() => {
  console.log(analyticsData.value)
  return analyticsData.value?.totalCountForCurrentPeriod || 0
})

const previousPeriod = computed(() => {
  return analyticsData.value?.totalCountForPreviousPeriod || 0
})

const percentageChange = computed(() => {
  const currentCount = analyticsData.value?.totalCountForCurrentPeriod || 0
  const previousCount = analyticsData.value?.totalCountForPreviousPeriod || 0

  return previousCount > 0
    ? (((currentCount - previousCount) / previousCount) * 100).toFixed(2)
    : 0
})
const chartOptions = ref({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
      position: 'top' as const,
    },
    title: {
      display: true,
    },
    tooltip: {
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      padding: 12,
      cornerRadius: 8,
      displayColors: false,
    },
  },
  scales: {
    x: {
      display: true,
      grid: {
        display: false,
      },
    },
    y: {
      display: true,
      beginAtZero: true,
      grid: {
        color: 'rgba(0, 0, 0, 0.05)',
      },
    },
  },
  animation: {
    duration: 2000,
    easing: 'easeInOutQuart' as const,
  },
  borderRadius: 8,
})

const businessesData = computed(() => {
  if (!analyticsData.value?.top20ForCurrentPeriod) {
    return []
  }

  // Sort by count and take top 5
  return [...analyticsData.value.top20ForCurrentPeriod]
    .sort((a, b) => b.count - a.count)
    .slice(0, 5)
})

const chartData = computed(() => {
  if (!analyticsData.value?.top20ForCurrentPeriod) {
    return {
      labels: [],
      datasets: [
        {
          label: '',
          backgroundColor: 'rgba(151, 186, 159, 0.8)',
          borderColor: 'rgba(151, 186, 159, 1)',
          borderWidth: 1,
          data: [],
        },
      ],
    }
  }

  const primaryColor = getComputedStyle(document.documentElement)
    ?.getPropertyValue('--p-primary-200')
    ?.trim()

  const labels = analyticsData.value.top20ForCurrentPeriod.map(item => item.businessName)

  return {
    labels,
    datasets: [
      {
        label: `Total views last ${matchOption.value} days`,
        backgroundColor: `${primaryColor}CC`,
        borderColor: primaryColor,
        borderWidth: 1,
        data: analyticsData.value.top20ForCurrentPeriod.map(item => item.count),
      },
    ],
  }
})

// Navigation function
const openBusiness = (businessId: string) => {
  navigateTo(`/businesses/${businessId}/details/analytics`)
}

const options = [
  {
    label: 'Last 7 days',
    value: 'LAST_7_DAYS',
    numberOfDays: 7,
  },
  {
    label: 'Last 30 days',
    value: 'LAST_30_DAYS',
    numberOfDays: 30,
  },
]

const selectedOption = ref(options[1].value)

const getData = async () => {
  isLoading.value = true
  const { getAnalyticsByWorkspace } = usePageAnalytics()
  const response = await getAnalyticsByWorkspace(selectedOption.value)
  console.log(response)
  analyticsData.value = response.data as AnalyticsData
  isLoading.value = false
}

const handleOptionChange = () => {
  getData()
}
const matchOption = computed(() => {
  const days = options.find(option => option.value === selectedOption.value)?.numberOfDays
  return days
})

const compareToText = computed(() => {
  const days = options.find(option => option.value === selectedOption.value)?.numberOfDays
  return days
})

onMounted(async () => {
  getData()
})
</script>
